import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { PropTypes as PropTypes$1 } from '@panneau/core';
import { isMessage, getPathValue } from '@panneau/core/utils';
import ResourceCard from '@panneau/element-resource-card';
import Select from '@panneau/field-select';

var propTypes = {
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  // eslint-disable-line react/forbid-prop-types
  errors: PropTypes$1.formErrors,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired
  })),
  maxItemsCount: PropTypes.number,
  loadItems: PropTypes.func,
  paginated: PropTypes.bool,
  requestUrl: PropTypes.string,
  requestOptions: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestQuery: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestSearchParamName: PropTypes.string,
  getItemLabel: PropTypes.func,
  getItemDescription: PropTypes.func,
  getItemImage: PropTypes.func,
  getItemId: PropTypes.func,
  getNewItem: PropTypes.func,
  itemLabelPath: PropTypes.string,
  itemDescriptionPath: PropTypes.string,
  itemImagePath: PropTypes.string,
  itemIdPath: PropTypes.string,
  itemLabelWithId: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'lg']),
  placeholder: PropTypes.string,
  creatable: PropTypes.bool,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  isRow: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  onChange: PropTypes.func,
  onCreate: PropTypes.func
};
var defaultProps = {
  name: null,
  value: null,
  errors: null,
  items: null,
  maxItemsCount: null,
  loadItems: null,
  paginated: false,
  requestUrl: null,
  requestOptions: null,
  requestQuery: null,
  requestSearchParamName: 'search',
  getItemLabel: getPathValue,
  getItemDescription: getPathValue,
  getItemImage: getPathValue,
  getItemId: getPathValue,
  getNewItem: null,
  itemLabelPath: 'label',
  itemDescriptionPath: null,
  itemImagePath: 'image.thumbnail_url',
  itemIdPath: 'id',
  itemLabelWithId: false,
  size: null,
  placeholder: null,
  creatable: false,
  multiple: false,
  disabled: false,
  isRow: false,
  className: null,
  inputClassName: null,
  onChange: null,
  onCreate: null
};
var ItemField = function ItemField(_ref) {
  var name = _ref.name,
    value = _ref.value,
    errors = _ref.errors,
    size = _ref.size,
    placeholder = _ref.placeholder,
    items = _ref.items,
    maxItemsCount = _ref.maxItemsCount,
    loadItems = _ref.loadItems,
    paginated = _ref.paginated,
    requestUrl = _ref.requestUrl,
    requestQuery = _ref.requestQuery,
    requestOptions = _ref.requestOptions,
    requestSearchParamName = _ref.requestSearchParamName,
    initialGetItemLabel = _ref.getItemLabel,
    getItemDescription = _ref.getItemDescription,
    getItemImage = _ref.getItemImage,
    getItemId = _ref.getItemId,
    getNewItem = _ref.getNewItem,
    itemLabelPath = _ref.itemLabelPath,
    itemDescriptionPath = _ref.itemDescriptionPath,
    itemImagePath = _ref.itemImagePath,
    itemIdPath = _ref.itemIdPath,
    itemLabelWithId = _ref.itemLabelWithId,
    creatable = _ref.creatable,
    multiple = _ref.multiple,
    disabled = _ref.disabled,
    isRow = _ref.isRow,
    className = _ref.className,
    inputClassName = _ref.inputClassName,
    onChange = _ref.onChange,
    onCreate = _ref.onCreate;
  var intl = useIntl();
  var _useState = useState(value || null),
    _useState2 = _slicedToArray(_useState, 1),
    initialValue = _useState2[0];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    createdItems = _useState4[0],
    setCreatedItems = _useState4[1];
  var getItemLabel = useCallback(function (it, path) {
    var id = getItemId(it, itemIdPath);
    if (itemLabelWithId) {
      var label = initialGetItemLabel(it, path);
      return label ? "".concat(label, " (#").concat(id, ")") : "#".concat(id);
    }
    return path !== null ? initialGetItemLabel(it, path) : "#".concat(id);
  }, [initialGetItemLabel, itemLabelWithId, getItemId, itemIdPath]);
  var getOptionValue = useCallback(function (it) {
    if (isString(it)) {
      return it;
    }
    var _ref2 = it || {},
      newValue = _ref2.value,
      _ref2$__isNew__ = _ref2.__isNew__,
      isNew = _ref2$__isNew__ === void 0 ? false : _ref2$__isNew__;
    if (isNew) {
      return newValue;
    }
    return getItemId(it, itemIdPath);
  }, [getItemId, itemIdPath]);
  var getOptionLabel = useCallback(function (it) {
    if (isString(it)) {
      return it;
    }
    var _ref3 = it || {},
      newLabel = _ref3.label,
      _ref3$__isNew__ = _ref3.__isNew__,
      isNew = _ref3$__isNew__ === void 0 ? false : _ref3$__isNew__;
    if (isNew) {
      return newLabel;
    }
    var label = getItemLabel(it, itemLabelPath);
    var description = getItemDescription(it, itemDescriptionPath);
    return description !== null ? "".concat(label, ": ").concat(description) : label;
  }, [getItemLabel, itemLabelPath, getItemDescription, itemDescriptionPath]);
  var getOptions = useCallback(function (newItems) {
    return [].concat(_toConsumableArray(newItems || []), _toConsumableArray(createdItems || []));
  }, [initialValue, createdItems]);
  var loadOptions = useMemo(function () {
    return loadItems !== null ? function (requestValue) {
      return loadItems(requestValue).then(function (newItems) {
        return getOptions(newItems);
      });
    } : null;
  }, [getOptions]);
  var onClickRemove = useCallback(function () {
    if (onChange !== null) {
      onChange(null);
    }
  }, [onChange]);
  var onCreateOption = useCallback(function (newLabel) {
    var newItem = getNewItem !== null ? getNewItem(newLabel) : newLabel;
    setCreatedItems([].concat(_toConsumableArray(createdItems || []), [newItem]));
    if (onChange !== null) {
      onChange(multiple ? [].concat(_toConsumableArray(value || []), [newItem]) : newItem);
    }
  }, [onChange, createdItems, getNewItem, setCreatedItems]);
  var options = useMemo(function () {
    return items !== null ? getOptions(items) : null;
  }, [items, getOptions]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(['position-relative', _defineProperty({}, className, className != null)])
  }, value !== null && !multiple ? /*#__PURE__*/React.createElement(ResourceCard, {
    className: "flex-grow-1",
    item: value,
    getItemLabel: initialGetItemLabel,
    getItemDescription: getItemDescription,
    getItemImage: getItemImage,
    itemLabelPath: itemLabelPath,
    itemDescriptionPath: itemDescriptionPath,
    itemImagePath: itemImagePath,
    itemLabelWithId: itemLabelWithId,
    disabled: disabled,
    onClickRemove: onClickRemove
  }) : /*#__PURE__*/React.createElement("div", {
    className: classNames([{
      row: isRow,
      'align-items-center': isRow
    }])
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-auto flex-grow-1"
  }, /*#__PURE__*/React.createElement(Select, Object.assign({
    className: classNames(['py-1', 'shadow-none', _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, disabled, disabled), 'is-invalid', errors !== null), "form-control", size !== null), "form-control-".concat(size), size !== null), inputClassName, inputClassName !== null)]),
    disabled: disabled,
    name: name,
    value: value,
    maxOptionsCount: maxItemsCount,
    paginated: paginated,
    requestUrl: requestUrl,
    requestQuery: requestQuery,
    requestOptions: requestOptions,
    requestSearchParamName: requestSearchParamName,
    creatable: creatable,
    onCreateOption: creatable ? onCreate || onCreateOption : null,
    isClearable: true,
    isSearchable: true,
    placeholder: isMessage(placeholder) ? intl.formatMessage(placeholder) : /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "oc3YLT",
      defaultMessage: [{
        "type": 0,
        "value": "Choose an item"
      }]
    }),
    onChange: onChange,
    getOptionValue: getOptionValue,
    getOptionLabel: getOptionLabel,
    prepareRequestOptions: getOptions,
    loadOptions: loadOptions,
    multiple: multiple,
    valueIsOption: true
  }, options !== null ? {
    options: options
  } : null)))));
};
ItemField.propTypes = propTypes;
ItemField.defaultProps = defaultProps;

var definition = {
  id: 'item',
  component: 'Item'
};

export { ItemField as default, definition };
